import API from '../api/api';
import { checkIsResponseSuccess } from '../utils/api';
import tokenService from './tokenService';
import storageService from './storageService';
import axiosInstance from '../http/axiosInstance';
const AUTH_CONSTANTS = {
    REFRESH_INTERVAL: 60 * 1000,
    TOKEN_EXPIRATION_THRESHOLD: 60,
};
const getUserLocalStorageKeys = (prefix = '') => {
    const normalizedPrefix = prefix ? `_${prefix}` : '';
    return {
        CURRENT_USER: `redtrack${normalizedPrefix}_cu`,
        SHARED_USER: `redtrack${normalizedPrefix}_su`,
        HEADERS_USER_TOKENS: `redtrack${normalizedPrefix}_hut`,
        HEADERS_SHARED_TOKENS: `redtrack${normalizedPrefix}_hst`,
        SILENT_MODE: `mode`,
        USER: 'user',
    };
};
export class AuthService {
    constructor(config) {
        if (!config.baseUrl) {
            throw new Error('baseUrl is required in AuthConfig');
        }
        this.intervalId = null;
        this.intervalSharedId = null;
        this.config = config;
    }
    getAppPrefix() {
        return this.config.appPrefix || '';
    }
    setSession({ userId = '', headers = {
        authorization: '',
        refresh: '',
    }, }) {
        if (!this.config) {
            console.error('AuthService not properly initialized');
            return false;
        }
        const { appPrefix } = this.config;
        const { CURRENT_USER, HEADERS_USER_TOKENS, SHARED_USER, HEADERS_SHARED_TOKENS, SILENT_MODE, } = getUserLocalStorageKeys(appPrefix);
        try {
            if (userId && headers) {
                storageService.setItem(CURRENT_USER, userId);
                storageService.setItem(HEADERS_USER_TOKENS, headers);
                if (headers.authorization) {
                    axiosInstance.defaults.headers.common.Authorization =
                        headers.authorization;
                }
            }
            else {
                storageService.removeItems([
                    CURRENT_USER,
                    HEADERS_USER_TOKENS,
                    SHARED_USER,
                    HEADERS_SHARED_TOKENS,
                    SILENT_MODE,
                ]);
                axiosInstance.defaults.headers.common.Authorization = '';
            }
            return true;
        }
        catch (error) {
            console.error('Session setup error:', error);
            return false;
        }
    }
    getSession() {
        const { appPrefix } = this.config;
        try {
            const userId = storageService.getItem(getUserLocalStorageKeys(appPrefix).CURRENT_USER);
            const headers = storageService.getItem(getUserLocalStorageKeys(appPrefix).HEADERS_USER_TOKENS);
            return { userId, headers };
        }
        catch (error) {
            console.error('Session get error:', error);
            return { userId: null, headers: null };
        }
    }
    removeSession() {
        const { appPrefix } = this.config;
        const { CURRENT_USER, HEADERS_USER_TOKENS, SHARED_USER, HEADERS_SHARED_TOKENS, SILENT_MODE, USER, } = getUserLocalStorageKeys(appPrefix);
        try {
            storageService.removeItems([
                CURRENT_USER,
                HEADERS_USER_TOKENS,
                SHARED_USER,
                HEADERS_SHARED_TOKENS,
                SILENT_MODE,
                USER,
            ]);
            axiosInstance.defaults.headers.common.Authorization = '';
            return true;
        }
        catch (error) {
            console.error('Session remove error:', error);
            return false;
        }
    }
    // Login method
    async loginCall(email, password, otherUrlParams = {}) {
        return await API({
            baseUrl: this.config.baseUrl || '',
            appPrefix: this.config.appPrefix || '',
        }).AUTH.login({
            login: email,
            password,
            ...otherUrlParams,
        });
    }
    // Logout method
    async logoutCall({ status = 0, headers = {
        authorization: '',
        refresh: '',
    }, otherUrlParams = {}, }) {
        const session = this.getSession();
        const authorization = session.headers?.authorization || headers.authorization || '';
        const refreshToken = session.headers?.refresh || headers.refresh || '';
        return await API({
            userStatus: status,
            headers: {
                authorization: authorization,
                'x-refresh-token': refreshToken,
            },
            baseUrl: this.config.baseUrl || '',
            appPrefix: this.config.appPrefix || '',
        }).AUTH.logOut({
            ...otherUrlParams,
        }, {
            Authorization: authorization,
            'x-refresh-token': refreshToken,
        });
    }
    // Refresh token
    async refreshCall(headers) {
        return await API({
            baseUrl: this.config.baseUrl || '',
            appPrefix: this.config.appPrefix || '',
        }).AUTH.refreshToken({}, headers);
    }
    async loadCurrentUser(headers) {
        try {
            return await API({
                baseUrl: this.config.baseUrl || '',
                appPrefix: this.config.appPrefix || '',
                headers: {
                    authorization: headers.Authorization,
                    'x-refresh-token': headers['x-refresh-token'],
                },
            }).USER.load({
                authorization: headers.Authorization,
                'x-refresh-token': headers['x-refresh-token'],
            });
        }
        catch (error) {
            console.error('Error loading current user:', error);
            throw error;
        }
    }
    async loadAndSetCurrentUser(headers, setUser) {
        try {
            const response = await this.loadCurrentUser({
                Authorization: headers.authorization,
                'x-refresh-token': headers.refresh,
            });
            if (response?.data) {
                setUser(response.data);
            }
        }
        catch (error) {
            console.error('Failed to load current user:', error);
        }
    }
    async loginBecome(params, handleAuthSuccess) {
        const { token, refreshToken } = params;
        try {
            storageService.setItem('mode', 'silent');
            const headers = {
                Authorization: token,
                'x-refresh-token': refreshToken,
            };
            const response = await this.loadCurrentUser(headers);
            if (response?.data?.id) {
                const user = response.data;
                this.setSession({
                    userId: user.id,
                    headers: {
                        authorization: token,
                        refresh: refreshToken,
                    },
                });
                await handleAuthSuccess({ user, headers });
            }
            else {
                throw new Error('Invalid user data received');
            }
        }
        catch (error) {
            console.error('Login become error:', error);
            throw error;
        }
    }
    async loadUserByApiKey(apiKey) {
        try {
            return await API({
                baseUrl: this.config.baseUrl || '',
                appPrefix: this.config.appPrefix || '',
            }).USER.loadByApiKey(apiKey);
        }
        catch (error) {
            console.error('Error loading user by API key:', error);
            throw error;
        }
    }
    async loginBecomePub({ apiKey }, handleAuthSuccess) {
        try {
            const response = await this.loadUserByApiKey(apiKey);
            if (response?.data?.apikey) {
                const user = response.data;
                storageService.setItem('user', user);
                this.setSession({
                    userId: user.id,
                    headers: {
                        authorization: '',
                        refresh: '',
                    },
                });
                await handleAuthSuccess({ user });
            }
            else {
                throw new Error('Invalid user data received');
            }
        }
        catch (error) {
            console.error('Login become PUB error:', error);
            throw error;
        }
    }
    /**
     * Clear interval if it exists
     */
    clearRefreshInterval(intervalId) {
        if (this[intervalId]) {
            clearInterval(this[intervalId]);
            this[intervalId] = null;
        }
    }
    /**
     * Set up token refresh interval
     * @param config - Configuration for token refresh
     * @returns Cleanup function to clear interval
     */
    refreshTokens({ authorization, refresh, intervalType, refreshAction, refreshMethod, onRefreshError, }) {
        if (!tokenService.validateTokens({ authorization, refresh })) {
            return () => null;
        }
        const intervalId = intervalType === 'main' ? 'intervalId' : 'intervalSharedId';
        // Clear existing interval if any
        if (this[intervalId]) {
            clearInterval(this[intervalId]);
        }
        // Set up new interval
        this[intervalId] = setInterval(() => {
            void (async () => {
                // Get current tokens based on interval type
                let currentAuthorization;
                let currentRefresh;
                if (intervalType === 'main') {
                    const session = this.getSession();
                    currentAuthorization =
                        session.headers?.authorization || authorization;
                    currentRefresh = session.headers?.refresh || refresh;
                }
                else {
                    const sharedSession = this.getSharedSession();
                    currentAuthorization =
                        sharedSession.sharedTokens?.authorization || authorization;
                    currentRefresh = sharedSession.sharedTokens?.refresh || refresh;
                    if (!sharedSession.sharedUserId) {
                        this.clearRefreshInterval(intervalId);
                        return;
                    }
                }
                if (!currentAuthorization || !currentRefresh) {
                    this.clearRefreshInterval(intervalId);
                    return;
                }
                const shouldRefreshAuth = tokenService.shouldRefresh(currentAuthorization, AUTH_CONSTANTS.TOKEN_EXPIRATION_THRESHOLD);
                const shouldRefreshToken = tokenService.shouldRefresh(currentRefresh, AUTH_CONSTANTS.TOKEN_EXPIRATION_THRESHOLD);
                const isAuthExpired = tokenService.isExpired(currentAuthorization);
                const isRefreshValid = !tokenService.isExpired(currentRefresh);
                if ((isAuthExpired && isRefreshValid) ||
                    shouldRefreshAuth ||
                    shouldRefreshToken) {
                    try {
                        const response = await refreshAction(currentRefresh);
                        const headers = response.headers;
                        if (checkIsResponseSuccess(response?.status)) {
                            refreshMethod(headers.authorization, headers['x-refresh-token']);
                        }
                        else {
                            this.clearRefreshInterval(intervalId);
                            onRefreshError('Refresh token failed');
                        }
                    }
                    catch (error) {
                        this.clearRefreshInterval(intervalId);
                        onRefreshError(error);
                    }
                }
            })();
        }, AUTH_CONSTANTS.REFRESH_INTERVAL);
        // Return cleanup function
        return () => this.clearRefreshInterval(intervalId);
    }
    refresh(authorization = '', refresh = '', onRefreshError) {
        return this.refreshTokens({
            authorization: authorization,
            refresh: refresh,
            intervalType: 'main',
            refreshAction: async (r) => {
                return await this.refreshCall({ 'x-refresh-token': r });
            },
            refreshMethod: (authorization, refresh) => {
                const { appPrefix } = this.config;
                const keys = getUserLocalStorageKeys(appPrefix);
                // Update main user tokens in localStorage
                // This allows main tokens to refresh even when in shared mode
                const updatedHeaders = {
                    authorization,
                    refresh,
                };
                storageService.setItem(keys.HEADERS_USER_TOKENS, updatedHeaders);
                // Don't update Authorization header if in shared mode
                const sharedSession = this.getSharedSession();
                if (sharedSession.sharedUserId) {
                    return;
                }
                if (authorization) {
                    axiosInstance.defaults.headers.common.Authorization = authorization;
                }
            },
            onRefreshError,
        });
    }
    reLoginByStorage(user, headersTokens, formatMessage, cbRouting = null) {
        return async () => {
            try {
                if (user?.id &&
                    tokenService.validateTokens({
                        authorization: headersTokens?.authorization || '',
                        refresh: headersTokens?.xRefreshToken || '',
                    })) {
                    if (user?.id) {
                        this.setSession({
                            userId: user?.id || '',
                            headers: {
                                authorization: headersTokens?.authorization || '',
                                refresh: headersTokens?.xRefreshToken || '',
                            },
                        });
                        if (cbRouting) {
                            await cbRouting();
                        }
                    }
                }
            }
            catch (error) {
                console.error('Re-login error:', error);
                throw error;
            }
        };
    }
    // Shared User Methods
    /**
     * Get shared session data from localStorage
     */
    getSharedSession() {
        const { appPrefix } = this.config;
        const keys = getUserLocalStorageKeys(appPrefix);
        try {
            const sharedUserId = storageService.getItem(keys.SHARED_USER);
            const sharedTokens = storageService.getItem(keys.HEADERS_SHARED_TOKENS);
            return { sharedUserId, sharedTokens };
        }
        catch (error) {
            console.error('Get shared session error:', error);
            return {
                sharedUserId: null,
                sharedTokens: null,
            };
        }
    }
    async loginShared(id, handleAuthSuccess) {
        try {
            const response = (await API({
                baseUrl: this.config.baseUrl || '',
                appPrefix: this.config.appPrefix || '',
            }).AUTH.loginShared(id));
            if (checkIsResponseSuccess(response?.status) && response.data?.user) {
                const sharedUser = response.data.user;
                const deleted_session = response.data.deleted_session;
                const sharedHeaders = {
                    authorization: response.headers.authorization,
                    refresh: response.headers['x-refresh-token'],
                };
                const { appPrefix } = this.config;
                const keys = getUserLocalStorageKeys(appPrefix);
                storageService.setItem(keys.SHARED_USER, id);
                storageService.setItem(keys.HEADERS_SHARED_TOKENS, sharedHeaders);
                storageService.setItem(keys.CURRENT_USER, sharedUser.id);
                if (sharedHeaders.authorization) {
                    axiosInstance.defaults.headers.common.Authorization =
                        sharedHeaders.authorization;
                }
                await handleAuthSuccess({
                    user: sharedUser,
                    headers: sharedHeaders,
                    sharedUserId: id,
                    deleted_session,
                });
            }
            else {
                throw new Error('Failed to login as shared user');
            }
        }
        catch (error) {
            console.error('Shared login error:', error);
            throw error;
        }
    }
    /**
     * Logout from shared user and restore main user
     */
    async logoutShared(mainTokens) {
        try {
            const { appPrefix } = this.config;
            const keys = getUserLocalStorageKeys(appPrefix);
            const sharedSession = this.getSharedSession();
            let logoutResponse = null;
            const sharedTokens = sharedSession.sharedTokens;
            if (sharedTokens) {
                logoutResponse = await API({
                    baseUrl: this.config.baseUrl || '',
                    appPrefix: this.config.appPrefix || '',
                }).AUTH.logOut({ current_session: true }, {
                    Authorization: sharedTokens.authorization,
                    'x-refresh-token': sharedTokens.refresh,
                });
            }
            if (!(logoutResponse && checkIsResponseSuccess(logoutResponse?.status))) {
                return null;
            }
            const mainUserResponse = await this.loadCurrentUser({
                Authorization: mainTokens.authorization,
                'x-refresh-token': mainTokens.refresh,
            });
            const mainUser = mainUserResponse.data;
            storageService.removeItems([
                keys.SHARED_USER,
                keys.HEADERS_SHARED_TOKENS,
            ]);
            if (this.intervalSharedId) {
                clearInterval(this.intervalSharedId);
                this.intervalSharedId = null;
            }
            storageService.setItem(keys.CURRENT_USER, mainUser.id);
            storageService.setItem(keys.HEADERS_USER_TOKENS, mainTokens);
            if (mainTokens.authorization) {
                axiosInstance.defaults.headers.common.Authorization =
                    mainTokens.authorization;
            }
            return {
                user: mainUser,
                response: logoutResponse,
            };
        }
        catch (error) {
            console.error('Shared logout error:', error);
            throw error;
        }
    }
    /**
     * Refresh shared user tokens
     */
    refreshShared(authorization = '', refresh = '', onRefreshError) {
        return this.refreshTokens({
            authorization: authorization,
            refresh: refresh,
            intervalType: 'shared',
            refreshAction: async (r) => {
                return await this.refreshCall({ 'x-refresh-token': r });
            },
            refreshMethod: (authorization, refresh) => {
                const { appPrefix } = this.config;
                const keys = getUserLocalStorageKeys(appPrefix);
                const sharedSession = this.getSharedSession();
                if (!sharedSession.sharedUserId) {
                    return;
                }
                const updatedHeaders = {
                    authorization,
                    refresh,
                };
                storageService.setItem(keys.HEADERS_SHARED_TOKENS, updatedHeaders);
                if (authorization) {
                    axiosInstance.defaults.headers.common.Authorization = authorization;
                }
            },
            onRefreshError,
        });
    }
}
//# sourceMappingURL=AuthService.js.map